import React, { useState } from 'react';
import { Card, Empty, HowCalculated, DefinitionTable } from '../MetricsUI';

/*
 * Section 3 — which material the org has reached, and which material fights back.
 *
 * Four views behind one set of tabs rather than four stacked cards: they answer
 * the same question from different angles and are read one at a time.
 *
 * Every bar is HTML, so the numbers and ticks beside them stay crisp at any
 * width — an SVG stretched to a fluid container squashes its own text.
 */

const TABS = [
  { key: 'chapters', label: 'Chapter reach & drop-off' },
  { key: 'cases', label: 'Case study difficulty' },
  { key: 'avatars', label: 'Avatar difficulty' },
  { key: 'quizzes', label: 'Quiz difficulty' },
];

/** Harder scores are redder. Bands, not a gradient, so two similar bars group. */
const scoreColor = (pct) => {
  if (pct == null) return 'bg-gray-200';
  if (pct < 60) return 'bg-rose-400';
  if (pct < 71) return 'bg-amber-400';
  if (pct < 76) return 'bg-yellow-300';
  return 'bg-emerald-500';
};
const cellColor = (pct) => {
  if (pct == null) return 'bg-gray-50 text-gray-300';
  if (pct < 55) return 'bg-rose-300 text-rose-950';
  if (pct < 62) return 'bg-orange-300 text-orange-950';
  if (pct < 72) return 'bg-yellow-200 text-yellow-900';
  if (pct < 80) return 'bg-emerald-300 text-emerald-950';
  return 'bg-emerald-500 text-white';
};

/**
 * One horizontal bar with an optional darker tick inside it.
 *
 * The tick is a second measure on the same scale — completion inside reach, or
 * first-attempt score inside the overall average. Two bars would take twice the
 * height and make the comparison harder, which is the whole point of the row.
 */
function Bar({ label, pct, tickPct, n, color, nLabel = 'n', onLabelClick }) {
  return (
    <div className="flex items-center gap-3 py-1">
      <div className="w-[210px] shrink-0 truncate text-sm text-gray-700" title={label}>
        {onLabelClick ? (
          <button onClick={onLabelClick} className="truncate text-left hover:text-lavender hover:underline">
            {label}
          </button>
        ) : (
          label
        )}
      </div>
      <div className="relative h-5 flex-1 overflow-hidden rounded bg-gray-100">
        {pct != null && (
          <div className={`h-full rounded ${color}`} style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
        )}
        {tickPct != null && (
          <div
            className="absolute top-0 h-full w-[2px] bg-navy/70"
            style={{ left: `${Math.max(0, Math.min(100, tickPct))}%` }}
            title={`${Math.round(tickPct)}%`}
          />
        )}
      </div>
      <div className="w-12 shrink-0 text-right text-sm font-bold tabular-nums text-navy">
        {pct == null ? '—' : `${Math.round(pct)}%`}
      </div>
      <div className="w-14 shrink-0 text-right text-xs tabular-nums text-gray-400">
        {nLabel}={n}
      </div>
    </div>
  );
}

export default function ContentCoverage({ content, onOpenLearner }) {
  const [tab, setTab] = useState('chapters');
  if (!content) return null;

  const { chapters, caseStudies, quizzes, avatarMatrix } = content;

  return (
    <Card>
      <div className="mb-4 flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              tab === t.key ? 'bg-navy text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'chapters' && (
        <div>
          <p className="mb-3 text-sm font-medium text-gray-700">
            Chapter reach &amp; drop-off
            <HowCalculated wide label="What is plotted">
              <DefinitionTable
                rows={[
                  ['Bar', 'People who have started the chapter, as a % of those who have it in scope'],
                  ['Dark tick', 'People who have completed it, on the same scale'],
                  ['Started', 'Any lesson watched, quiz submitted or case study scored'],
                  ['Complete', 'All lessons watched, all quizzes submitted, one case study scored'],
                  ['n', 'People with this chapter in their learning path'],
                ]}
              />
              <span className="block text-gray-500">
                The denominator is per chapter, so an advanced cohort skipping Chapter 2 is not a drop-off.
              </span>
            </HowCalculated>
          </p>
          <div className="max-h-[520px] overflow-y-auto pr-1">
            {chapters.length === 0 ? (
              <Empty>No chapters in scope.</Empty>
            ) : (
              chapters.map((c) => (
                <Bar
                  key={c.chapterId}
                  label={`${c.number}. ${c.title}`}
                  pct={c.startedPct}
                  tickPct={c.completedPct}
                  n={c.inScope}
                  color="bg-lavender"
                />
              ))
            )}
          </div>
        </div>
      )}

      {tab === 'cases' && (
        <div>
          <p className="mb-3 text-sm font-medium text-gray-700">
            Case study difficulty &mdash; hardest first
            <HowCalculated wide label="What is plotted">
              <DefinitionTable
                rows={[
                  ['Bar', 'Mean score over every scored attempt by anyone in view'],
                  ['Dark tick', "Mean of each person's FIRST scored attempt"],
                  ['Gap', 'Tick well left of the bar means the case teaches well on a retry'],
                  ['n', 'People who have scored it at least once'],
                  ['Order', 'Lowest average first; unattempted cases sort last'],
                ]}
              />
              <span className="block text-gray-500">
                A consistently low case is either genuinely hard or badly briefed — compare the tick against
                the bar before assuming it is the learners.
              </span>
            </HowCalculated>
          </p>
          {caseStudies.length === 0 ? (
            <Empty>No case studies in scope.</Empty>
          ) : (
            caseStudies.map((c) => (
              <Bar
                key={c.moduleIdSst}
                label={c.moduleName}
                pct={c.avgPct}
                tickPct={c.avgFirstPct}
                n={c.people}
                color={scoreColor(c.avgPct)}
              />
            ))
          )}
        </div>
      )}

      {tab === 'quizzes' && (
        <div>
          <p className="mb-3 text-sm font-medium text-gray-700">
            Quiz difficulty &mdash; lowest first
            <HowCalculated wide label="What is plotted">
              <DefinitionTable
                rows={[
                  ['Bar', "Mean of each person's FIRST attempt at that quiz"],
                  ['Why first', 'A retry measures persistence, not whether the lesson landed'],
                  ['n', 'People whose first attempt could be scored'],
                  ['Excluded', 'Quizzes still recorded as 1 out of 1 — see below'],
                ]}
              />
              <span className="block text-gray-500">
                Until the next Unity build, most quizzes are written as a single correct answer out of one.
                Those are left out rather than shown as 100%.
              </span>
            </HowCalculated>
          </p>
          {quizzes.filter((q) => q.avgFirstPct != null).length === 0 ? (
            <Empty>
              No quiz score can be trusted yet — quizzes are still recorded as 1 out of 1. This fills in with
              the next Unity build.
            </Empty>
          ) : (
            quizzes
              .filter((q) => q.avgFirstPct != null)
              .map((q) => (
                <Bar
                  key={q.moduleIdSst}
                  label={q.moduleName}
                  pct={q.avgFirstPct}
                  n={q.people}
                  color={scoreColor(q.avgFirstPct)}
                />
              ))
          )}
        </div>
      )}

      {tab === 'avatars' && <AvatarMatrix matrix={avatarMatrix} />}
    </Card>
  );
}

/**
 * Case study x AI avatar.
 *
 * Rendered even when empty, as a real grid of dashes. Hiding it would mean
 * nobody notices the day it starts working; showing dashes says plainly that the
 * shape is ready and the data is not.
 */
function AvatarMatrix({ matrix }) {
  const [hover, setHover] = useState(null);
  if (!matrix) return null;

  const { avatars, rows, perAvatar, available } = matrix;

  return (
    <div>
      <p className="mb-3 text-sm font-medium text-gray-700">
        Case study &times; AI avatar
        <HowCalculated wide label="What is plotted">
          <DefinitionTable
            rows={[
              ['Cell', 'Mean score for that case study against that avatar, as a %'],
              ['Colour', 'Red = harder, green = easier'],
              ['Dash', 'Nobody in view has played that pairing'],
              ['Cards below', "Each avatar's overall average, median and attempt count"],
              ['Why it matters', 'The same case against a different personality is a different rehearsal'],
            ]}
          />
        </HowCalculated>
      </p>

      {!available && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          No attempt in this organisation records which avatar was faced, so every cell is blank. The chart
          is wired and fills in on its own once the next Unity build starts sending it.
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] border-separate" style={{ borderSpacing: '3px' }}>
          <thead>
            <tr>
              <th className="w-[190px]" />
              {avatars.map((a) => (
                <th key={a} className="px-2 pb-1 text-center text-xs font-semibold text-gray-600">
                  {a}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.moduleIdSst}>
                <td className="w-[190px] truncate pr-2 text-sm text-gray-700" title={r.moduleName}>
                  {r.moduleName}
                </td>
                {r.cells.map((c) => (
                  <td key={c.avatarName} className="p-0">
                    <div
                      onMouseEnter={() => setHover({ row: r, cell: c })}
                      onMouseLeave={() => setHover(null)}
                      className={`flex h-8 items-center justify-center rounded text-xs font-semibold tabular-nums ${cellColor(
                        c.avgPct
                      )} ${
                        hover && hover.cell === c ? 'ring-2 ring-navy' : ''
                      }`}
                    >
                      {c.avgPct == null ? '—' : Math.round(c.avgPct)}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-2 flex min-h-[20px] items-center justify-between text-xs">
        <span>
          {hover ? (
            <>
              <b className="text-navy">{hover.row.moduleName}</b>
              <span className="text-gray-500"> vs </span>
              <b className="text-navy">{hover.cell.avatarName}</b>
              <span className="text-gray-500">
                {hover.cell.avgPct == null
                  ? ' — not attempted'
                  : ` — avg ${Math.round(hover.cell.avgPct)}% over ${hover.cell.attempts} attempt${
                      hover.cell.attempts === 1 ? '' : 's'
                    }`}
              </span>
            </>
          ) : (
            <span className="text-gray-400">Hover a cell for its detail.</span>
          )}
        </span>
        <span className="flex items-center gap-1.5 text-[10px] text-gray-400">
          harder
          <span className="h-3 w-4 rounded bg-rose-300" />
          <span className="h-3 w-4 rounded bg-orange-300" />
          <span className="h-3 w-4 rounded bg-yellow-200" />
          <span className="h-3 w-4 rounded bg-emerald-300" />
          <span className="h-3 w-4 rounded bg-emerald-500" />
          easier
        </span>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {perAvatar.map((a) => (
          <div key={a.avatarName} className="rounded-lg border border-gray-100 p-3">
            <p className="text-sm font-semibold text-navy">{a.avatarName}</p>
            <p className="mt-1 text-2xl font-bold tabular-nums text-navy">
              {a.avgPct == null ? '—' : `${Math.round(a.avgPct)}%`}
            </p>
            <p className="text-[11px] text-gray-400">
              {a.attempts} attempt{a.attempts === 1 ? '' : 's'}
              {a.medianPct != null && ` · median ${Math.round(a.medianPct)}%`}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
